import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { FileText, FileUp, Download, Eye, Fingerprint, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PdfPreview, downloadDoc } from "./PdfPreview";
import { DOC_KINDS, docKindLabel, fmtDateTime, fmtSize, shortHash } from "@/lib/contracts";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { toast } from "sonner";
import { translateFr as tfr } from "@/i18n";

const KIND_COLORS = { contract: "bg-red-500/10 text-red-400 border-red-500/25", annex: "bg-blue-500/10 text-blue-400 border-blue-500/25", amendment: "bg-amber-500/10 text-amber-400 border-amber-500/25", identity: "bg-purple-500/10 text-purple-400 border-purple-500/25", proof: "bg-cyan-500/10 text-cyan-400 border-cyan-500/25", other: "bg-zinc-500/10 text-zinc-400 border-zinc-500/25" };

export const TabDocuments = ({ contract, onChanged }) => {
  const inputRef = useRef(null);
  const [kind, setKind] = useState("contract");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const docs = contract.documents || [];

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 15 Mo)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_kind", kind);
      await api.post(`/contracts/${contract.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`« ${file.name} » ${tfr("ajouté — empreinte SHA-256 calculée")}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tfr("Échec de l'envoi")); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div className="space-y-4" data-testid="tab-documents">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
        className={cn("rounded-2xl border border-dashed p-6 sm:p-8 text-center transition-colors",
          dragOver ? "border-red-500/60 bg-red-500/[0.04]" : "border-border bg-surface")}
        data-testid="document-dropzone">
        <FileUp className="h-7 w-7 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm font-medium">{tfr("Glissez un document ici, ou")}</p>
        <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-[190px] h-9" data-testid="doc-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_KINDS.map((k) => <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="doc-upload-btn">
            {uploading ? tfr("Envoi…") : tfr("Choisir un fichier")}
          </Button>
          <input ref={inputRef} type="file" className="hidden" data-testid="doc-file-input"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.csv,.txt" onChange={(e) => upload(e.target.files?.[0])} />
        </div>
        <p className="text-xs text-muted-foreground mt-3">{tfr("PDF, DOCX, images… max 15 Mo. Chaque version est immuable et horodatée (SHA-256).")}</p>
      </div>

      {docs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="documents-empty">
          {tfr("Aucun document pour le moment.")}
        </div>
      ) : (
        <div className="space-y-2" data-testid="documents-list">
          {docs.map((d) => (
            <motion.div key={d.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
              className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-border bg-secondary/30 p-4 hover:border-foreground/25 transition-colors"
              data-testid={`document-row-${d.id}`}>
              <div className="h-10 w-10 rounded-lg bg-secondary/50 border border-border flex items-center justify-center shrink-0">
                <FileText className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium truncate">{d.filename}</p>
                  <span className={cn("text-xs rounded-full border px-2 py-0.5", KIND_COLORS[d.doc_kind] || KIND_COLORS.other)}>{docKindLabel(d.doc_kind)}</span>
                  <span className="text-xs text-muted-foreground rounded-full border border-border px-2 py-0.5">v{d.version}</span>
                  {d.is_primary && <span className="inline-flex items-center gap-1 text-xs text-amber-400"><Star className="h-3 w-3 fill-current" /> Principal</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span>{fmtSize(d.size)}</span><span className="opacity-40">·</span>
                  <span>{fmtDateTime(d.uploaded_at)}</span><span className="opacity-40">·</span>
                  <span className="inline-flex items-center gap-1 font-mono"><Fingerprint className="h-3 w-3" />{shortHash(d.sha256)}</span>
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button size="sm" variant="outline" className="h-8" onClick={() => setPreview(d)} data-testid={`doc-preview-${d.id}`}>
                  <Eye className="h-3.5 w-3.5 mr-1.5" /> Aperçu
                </Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => downloadDoc(contract.id, d).catch(() => toast.error(tfr("Téléchargement impossible")))} data-testid={`doc-download-${d.id}`}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={Boolean(preview)} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="sm:max-w-[840px]" data-testid="doc-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{preview?.filename}</DialogTitle>
            <DialogDescription>v{preview?.version} · {docKindLabel(preview?.doc_kind)} · SHA-256 {shortHash(preview?.sha256)}</DialogDescription>
          </DialogHeader>
          {preview && <PdfPreview cid={contract.id} doc={preview} height={560} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
